import {
  KNOWN_EVENT_CATEGORIES,
  MITRE_TACTICS,
  MitreTechniqueId,
  SEVERITY_ORDER,
  Severity,
  type SecurityEventSummary,
} from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { listEvents, type EventFilters } from '../api/events.js';
import { listServerGroups, listServers } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { EventPanel } from '../components/EventPanel.js';
import { DataTable } from '../ui/DataTable.js';
import { eventColumns } from '../components/event-columns.js';
import { EventRowDetail } from '../components/EventRowDetail.js';
import { PageHeader } from '../components/PageHeader.js';
import { effectiveTimezone } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * Security events (knowledge doc section 15: filter by server, group, category,
 * severity and time; paginated).
 *
 * The filters live in the URL rather than in component state, which is what makes
 * "the unacknowledged criticals on the web servers" a link somebody can send to a
 * colleague — and what makes the back button behave.
 *
 * Paging is forward-only over a stack of visited cursors, as the fleet list does.
 * A cursor is what stops a page being skipped or repeated because an agent reported
 * something while somebody was reading.
 */

const ALL = '__all__';

const RANGES = [
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
  /** Named rather than empty, because an absent parameter means the default. */
  { value: 'all', label: 'All time' },
] as const;

export function EventsPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const categoryListId = useId();
  const tacticListId = useId();

  const [params, setParams] = useSearchParams();
  /** The cursor for the page being shown, and the ones behind it. */
  const [history, setHistory] = useState<(string | undefined)[]>([undefined]);
  const cursor = history[history.length - 1];

  const read = (key: string): string => params.get(key) ?? '';
  const hours = params.get('hours') ?? '24';

  /** Any filter change starts the paging over; the old cursors are for other results. */
  const set = (key: string, value: string): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === '' || value === ALL) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
    setHistory([undefined]);
  };

  /**
   * The start of the window, pinned when the range changes rather than recomputed
   * on every render.
   *
   * Two reasons, and the second is the important one. A `from` built from
   * `Date.now()` inline is a new value each render, so the React Query key never
   * settles and the list refetches forever — which is how this was found. And a
   * window that slides while somebody is reading would mean page two is a query
   * over a different range from page one, which is exactly the skipped-or-repeated
   * row that cursor pagination exists to prevent.
   */
  const from = useMemo(
    () =>
      hours === 'all' ? undefined : new Date(Date.now() - Number(hours) * 3_600_000).toISOString(),
    [hours],
  );

  const severity = Severity.safeParse(read('min_severity'));
  const filters: EventFilters = {
    ...(read('server_id') === '' ? {} : { server_id: read('server_id') }),
    ...(read('group_id') === '' ? {} : { group_id: read('group_id') }),
    ...(read('category') === '' ? {} : { category: read('category') }),
    ...(read('tactic') === '' ? {} : { tactic: read('tactic') }),
    ...(read('technique') === '' ? {} : { technique: read('technique') }),
    ...(severity.success ? { min_severity: severity.data } : {}),
    ...(read('state') === 'open' ? { acknowledged: false } : {}),
    ...(read('state') === 'done' ? { acknowledged: true } : {}),
    ...(read('mine') === 'true' ? { assigned_to: 'me' as const } : {}),
    ...(from === undefined ? {} : { from }),
    ...(cursor === undefined ? {} : { cursor }),
  };

  const [open, setOpen] = useState<SecurityEventSummary | null>(null);
  const events = useQuery({
    queryKey: ['events', filters],
    queryFn: () => listEvents(filters),
    // Keeps the table on screen while the next page loads, rather than blanking.
    placeholderData: keepPreviousData,
  });

  const servers = useQuery({ queryKey: ['servers'], queryFn: () => listServers({ limit: 200 }) });
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  const anyFilter = [
    'server_id',
    'group_id',
    'category',
    'tactic',
    'technique',
    'min_severity',
    'state',
    'mine',
  ].some((key) => read(key) !== '');

  /** One box for both: a technique id (`T1070.004`) or a tactic's name (P11.8.6). */
  const setAttack = (value: string): void => {
    const trimmed = value.trim();
    const technique = MitreTechniqueId.safeParse(trimmed.toUpperCase());
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('tactic');
        next.delete('technique');
        if (technique.success) next.set('technique', technique.data);
        else if (value !== '') next.set('tactic', value);
        return next;
      },
      { replace: true },
    );
    setHistory([undefined]);
  };

  return (
    <div className="page">
      <PageHeader title="Events" description="What the agents have reported, newest first." />

      <div className="filters">
        <SelectField
          label="Server"
          value={read('server_id') === '' ? ALL : read('server_id')}
          onChange={(event) => {
            set('server_id', event.target.value);
          }}
        >
          <option value={ALL}>Any server</option>
          {(servers.data?.items ?? []).map((server) => (
            <option key={server.id} value={server.id}>
              {server.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Group"
          value={read('group_id') === '' ? ALL : read('group_id')}
          onChange={(event) => {
            set('group_id', event.target.value);
          }}
        >
          <option value={ALL}>Any group</option>
          {(groups.data ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Severity"
          hint="Shows this level and everything worse."
          value={read('min_severity') === '' ? ALL : read('min_severity')}
          onChange={(event) => {
            set('min_severity', event.target.value);
          }}
        >
          <option value={ALL}>Any severity</option>
          {[...SEVERITY_ORDER].reverse().map((option) => (
            <option key={option} value={option}>
              {option} and above
            </option>
          ))}
        </SelectField>

        {/* Free text with suggestions: section 6 says a category is a plain string,
            so an agent newer than this console must still be filterable. */}
        <TextField
          label="Category"
          type="search"
          list={categoryListId}
          placeholder="disk"
          value={read('category')}
          onChange={(event) => {
            set('category', event.target.value);
          }}
        />
        <datalist id={categoryListId}>
          {KNOWN_EVENT_CATEGORIES.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>

        {/* P11.8.6: the tags Wazuh's rules carry, a tactic or a technique id. */}
        <TextField
          label="MITRE ATT&CK"
          hint="A tactic, or a technique such as T1070.004."
          type="search"
          list={tacticListId}
          placeholder="Defense Evasion"
          value={read('technique') === '' ? read('tactic') : read('technique')}
          onChange={(event) => {
            setAttack(event.target.value);
          }}
        />
        <datalist id={tacticListId}>
          {MITRE_TACTICS.map((tactic) => (
            <option key={tactic} value={tactic} />
          ))}
        </datalist>

        <SelectField
          label="State"
          value={read('state') === '' ? ALL : read('state')}
          onChange={(event) => {
            set('state', event.target.value);
          }}
        >
          <option value={ALL}>Any state</option>
          <option value="open">Not acknowledged</option>
          <option value="done">Acknowledged</option>
        </SelectField>

        <SelectField
          label="Time"
          value={hours}
          onChange={(event) => {
            set('hours', event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.label} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>

        <label className="checkbox filter-toggle">
          <input
            type="checkbox"
            checked={read('mine') === 'true'}
            onChange={(event) => {
              set('mine', event.target.checked ? 'true' : '');
            }}
          />
          Assigned to me
        </label>
      </div>

      {events.isError && (
        <Alert tone="danger" title="Could not load events">
          {events.error instanceof Error ? events.error.message : 'Try again in a moment.'}
        </Alert>
      )}

      {events.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading events…
        </p>
      )}

      {events.data?.items.length === 0 && (
        <EmptyState title="No events match">
          {anyFilter
            ? 'Try widening the filters, or looking further back.'
            : 'Nothing has been reported in this period. That is the good outcome.'}
        </EmptyState>
      )}

      {events.data !== undefined && events.data.items.length > 0 && (
        <>
          <DataTable
            caption="Security events, with their severity, server and state"
            columns={eventColumns(timezone)}
            rows={events.data.items}
            rowKey={(event) => event.id}
            onSelect={setOpen}
            selectedKey={open?.id}
            renderDetail={(event) => <EventRowDetail event={event} timezone={timezone} />}
          />

          <div className="row">
            <Button
              disabled={history.length === 1}
              onClick={() => {
                setHistory((current) => current.slice(0, -1));
              }}
            >
              Previous
            </Button>
            <Button
              disabled={events.data.nextCursor === null}
              onClick={() => {
                const next = events.data.nextCursor;
                if (next !== null) setHistory((current) => [...current, next]);
              }}
            >
              Next
            </Button>
            <span className="muted text-sm">
              Page {history.length}
              {events.isFetching ? ' · loading…' : ''}
            </span>
          </div>
        </>
      )}

      {open !== null && (
        <EventPanel
          event={open}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
